import { createSlice } from '@reduxjs/toolkit';
import {
  getNowEnvironment,
  getDailyEnvironment,
  getLocalEnvironmentInfo,
  getNowEnvironments,
  getDailyEnvironments,
  getEnvironmentHistories
} from '../thunks/environment.thunk.ts';
import { ENVIRONMENT_SLICE_NAME } from '../actions/environment.action.ts';
import {
  IEnvironmentNow,
  IEnvironmentDaily,
  ReqFetchEnvironmentHistories,
  EnvironmentStatistics,
  IEnvironmentMeta
} from '../models/environment.model.ts';
import { TotalMapProps } from 'modules/common/index.ts';
import { dateToString } from 'utils/formatDateToString.js';
import dayjs from 'dayjs';
import { formatWeatherStatistics } from 'utils/weatherConverter.ts';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'constants/const.js';

export interface EnvironmentState {
  dashboardDate: Date;
  environmentNow: IEnvironmentNow;
  environmentNowStatistics: EnvironmentStatistics;

  environmentDaily: IEnvironmentDaily;
  environmentDailyStatistics: EnvironmentStatistics;
  dailyCumulativeRain: number;

  environmentMetas: IEnvironmentMeta[];
  environmentMetasTotalMap: TotalMapProps;

  fetchEnvironmentHistoriesRequest: ReqFetchEnvironmentHistories;
}

const initialState: EnvironmentState = {
  dashboardDate: dayjs().subtract(1, 'day').toDate(),
  environmentNow: {
    environmentTime: '',
    windDirectionMin: 0,
    windDirectionAverage: 0,
    windDirectionMax: 0,
    windSpeedMin: 0,
    windSpeedAverage: 0,
    windSpeedMax: 0,
    temperatureAir: 0,
    temperatureObj: 0,
    heatIndex: 0,
    relativeHumidity: 0,
    atmosphericPressure: 0,
    cumulativePrecipitation: 0,
    pm1: 0,
    pm2_5: 0,
    pm10: 0,
    dataKey: ''
  },

  dailyCumulativeRain: 0,
  environmentNowStatistics: {
    time: [],
    temperatureAir: [],
    windDirection: [],
    windSpeed: [],
    maxWindSpeed: [],
    humidity: [],
    barometer: [],
    rain: [],
    heatIndex: [],
    pm2_5: [],
    pm10: []
  },

  environmentDailyStatistics: {
    time: [],
    temperatureAir: [],
    windDirection: [],
    windSpeed: [],
    maxWindSpeed: [],
    humidity: [],
    barometer: [],
    rain: [],
    heatIndex: [],
    pm2_5: [],
    pm10: []
  },
  environmentMetas: [],
  environmentMetasTotalMap: {
    totalElements: 0,
    totalPages: 0
  },

  environmentDaily: {
    temperatureAirMin: 0,
    temperatureAirMax: 0,
    windDirectionMin: 0,
    windDirectionAvg: 0,
    windDirectionMax: 0,
    windSpeedMin: 0,
    windSpeedMax: 0,
    rain: 0,
    humidityMax: 0,
    barometerMax: 0,
    heatIndexMax: 0
  },
  fetchEnvironmentHistoriesRequest: {
    from: dateToString(dayjs().subtract(6, 'day').toDate()),
    to: dateToString(dayjs().toDate()),
    page: DEFAULT_PAGE,
    size: DEFAULT_PAGE_SIZE
  }
};

const environmentSlice = createSlice({
  name: ENVIRONMENT_SLICE_NAME,
  initialState: initialState,
  reducers: {
    changeDashboardDate: (state, action) => {
      state.dashboardDate = action.payload;
    },
    changeFetchEnvironmentHistoriesRequest: (state, action) => {
      state.fetchEnvironmentHistoriesRequest = {
        ...state.fetchEnvironmentHistoriesRequest,
        ...action.payload
      };
    },

    resetAll: () => initialState
  },
  extraReducers: (builder) => {
    builder.addCase(getEnvironmentHistories.fulfilled, (state, action) => {
      state.environmentMetas = action.payload.resultList;
      state.environmentMetasTotalMap = action.payload.totalMap;
    });

    builder.addCase(getNowEnvironment.fulfilled, (state, action) => {
      state.environmentNow = action.payload.resultObject;
    });

    builder.addCase(getNowEnvironments.fulfilled, (state, action) => {
      const result = action.payload.resultList;

      state.environmentMetas = result;
      state.environmentNowStatistics = formatWeatherStatistics(result);
    });

    builder.addCase(getDailyEnvironment.fulfilled, (state, action) => {
      state.environmentDaily = action.payload.resultObject;
    });

    builder.addCase(getDailyEnvironments.fulfilled, (state, action) => {
      const result = action.payload.resultList;

      state.dailyCumulativeRain = action.payload.resultObject.dailyCumulativeRain;
      state.environmentMetas = result;
      state.environmentDailyStatistics = formatWeatherStatistics(result);
    });

    builder.addCase(getLocalEnvironmentInfo.fulfilled, (state, action) => {});
  }
});

export const environment = environmentSlice.name;
export const environmentReducer = environmentSlice.reducer;
export const environmentActions = environmentSlice.actions;
export const { changeFetchEnvironmentHistoriesRequest, changeDashboardDate, resetAll } =
  environmentSlice.actions;
export default environmentSlice.reducer;
